/**
 * Feature Flag Defaults
 * -----------------------------------------------------------------------------
 * This file exports FEATURE_FLAG_DEFAULTS, a map of all flag names to their default values.
 * Built automatically from the registry, so new flags only require an entry in featureFlagRegistry.ts.
 */

import { FEATURE_FLAGS, FEATURE_FLAG_REGISTRY, FeatureFlagTypes } from "./featureFlagRegistry";

/**
 * Helper type to make a type fully mutable (remove readonly).
 */
type Mutable<T> = {
  -readonly [P in keyof T]: T[P];
};

/**
 * Default values for all feature flags.
 *
 * Built automatically from the registry, so new flags only require
 * an entry in `featureFlagRegistry.ts`.
 */
export const FEATURE_FLAG_DEFAULTS: Mutable<FeatureFlagTypes> = FEATURE_FLAGS.reduce<
  Mutable<FeatureFlagTypes>
>((acc, flag) => ({
  ...acc,
  [flag]: FEATURE_FLAG_REGISTRY[flag].default,
}), {} as Mutable<FeatureFlagTypes>);

/**
 * Validate that a value is a valid type for a specific feature flag.
 * Used for type-safe ECS data transformation.
 */
export function isValidFlagValue<K extends keyof FeatureFlagTypes>(
  flag: K,
  value: unknown
): value is FeatureFlagTypes[K] {
  const expectedType = typeof FEATURE_FLAG_REGISTRY[flag].default;
  return typeof value === expectedType;
}

/**
 * Get a safe copy of the default configuration.
 * This ensures modifications don't affect the original defaults.
 */
export function getDefaultConfig(): Mutable<FeatureFlagTypes> {
  return { ...FEATURE_FLAG_DEFAULTS };
}